/**
 * TicketList — sortable, filterable table of tickets with inline line-item expand.
 * Used by: CategoriesTab (tickets sub-tab), AllTicketsList, TicketsTab.
 *
 * Props:
 *   tickets — array of ticket objects
 */
import React, { useState, useMemo } from 'react';
import { fmt$, fmtN } from '../../../utils/formatUtils';
import { fmtTimeCst } from '../../../utils/cstUtils';
import { SortTh, useSortState, applySortStr, useColFilters, applyColFilters, FilterCell } from '../../../utils/tableUtils';
import { getTicketType, TICKET_TYPE_META, TKT_COL_DEFS } from '../squareDashboardUtils';
import TableContextMenu, { useContextMenu } from './TableContextMenu';
import { downloadCSV } from '../../../utils/excelUtils';

export default function TicketList({ tickets: ticketsProp, categories }) {
  // Use pre-built allTickets from the API when available; otherwise de-dup across categories.
  const ticketsRaw = useMemo(() => {
    if (ticketsProp) return ticketsProp;
    if (!categories) return [];
    const map = {};
    categories.forEach(cat => {
      (cat.ticketList || []).forEach(tkt => {
        if (!map[tkt.id]) {
          map[tkt.id] = { ...tkt, revenueCents: tkt.revenueCents || 0, discountCents: tkt.discountCents || 0, itemQty: 0, lines: [] };
        }
        const m = map[tkt.id];
        m.itemQty       += tkt.itemQty       || 0;
        m.revenueCents  += tkt.revenueCents  || 0;
        m.discountCents += tkt.discountCents || 0;
        (tkt.lines || []).forEach(line => {
          const ex = m.lines.find(l => l.name === line.name);
          if (ex) { ex.qty += line.qty; ex.revenueCents = (ex.revenueCents || 0) + (line.revenueCents || 0); }
          else    { m.lines.push({ ...line }); }
        });
      });
    });
    return Object.values(map).map(t => ({
      ...t,
      revenue:  (t.revenueCents  / 100).toFixed(2),
      discount: (t.discountCents / 100).toFixed(2),
    })).sort((a, b) => new Date(b.time) - new Date(a.time));
  }, [ticketsProp, categories]);

  const [expanded,    setExpanded]    = useState(null);
  const [search,      setSearch]      = useState('');
  const [filter,      setFilter]      = useState('all');
  const [typeFilter,  setTypeFilter]  = useState('all');
  const [sortKey, sortDir, onSort]    = useSortState('time');

  const [colFilters, setColFilter, clearFilters, hasActiveFilters] = useColFilters(TKT_COL_DEFS);

  const ticketsWithType = useMemo(
    () => (ticketsRaw || []).map(t => ({ ...t, ticketType: getTicketType(t) })),
    [ticketsRaw]
  );

  const typeCounts = useMemo(() => {
    const c = {};
    ticketsWithType.forEach(t => { c[t.ticketType] = (c[t.ticketType] || 0) + 1; });
    return c;
  }, [ticketsWithType]);

  const filtered = useMemo(() => {
    let list = ticketsWithType;
    if (filter !== 'all')     list = list.filter(t => t.status.toLowerCase() === filter);
    if (typeFilter !== 'all') list = list.filter(t => t.ticketType === typeFilter);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(t =>
        t.shortId.toLowerCase().includes(q) ||
        (t.ticketName || '').toLowerCase().includes(q) ||
        t.lines.some(l => l.name.toLowerCase().includes(q))
      );
    }
    list = applyColFilters(list, colFilters, TKT_COL_DEFS);
    return applySortStr(list, sortKey, sortDir);
  }, [ticketsWithType, filter, typeFilter, search, colFilters, sortKey, sortDir]);

  const [menuState, openMenu, closeMenu] = useContextMenu();
  const handleDownload = () => {
    const headers = ['Time (CST)', 'Ticket #', 'Name', 'Status', 'Type', 'Items', 'Revenue', 'Discount', 'Line Items'];
    const rows = filtered.map(tkt => [
      fmtTimeCst(tkt.time),
      tkt.shortId,
      tkt.ticketName || '',
      tkt.status,
      TICKET_TYPE_META[tkt.ticketType]?.label || tkt.ticketType,
      tkt.itemQty,
      tkt.revenue,
      tkt.discountCents > 0 ? tkt.discount : 0,
      (tkt.lines || []).map(l => `${l.name} x${l.qty}`).join('; '),
    ]);
    downloadCSV(headers, rows, `tickets-${new Date().toISOString().slice(0,10)}`);
  };

  if (!ticketsRaw || ticketsRaw.length === 0)
    return <p className="sq__empty">No tickets in this period.</p>;

  const tot = filtered.reduce(
    (a, t) => ({ items: a.items + t.itemQty, rev: a.rev + t.revenueCents, disc: a.disc + t.discountCents }),
    { items: 0, rev: 0, disc: 0 }
  );

  return (
    <>
      {/* ── top toolbar ── */}
      <div className="sq__tkt-toolbar">
        <input
          className="sq__search"
          type="search"
          placeholder="Ticket # or item…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ maxWidth: 200 }}
        />
        <div className="sq__fpills">
          {[{ k: 'all', l: 'All' }, { k: 'open', l: '🟡 Open' }, { k: 'completed', l: '🟢 Done' }].map(f => (
            <button
              key={f.k}
              className={`sq__fpill${filter === f.k ? ' active' : ''}`}
              onClick={() => setFilter(f.k)}
            >
              {f.l}
            </button>
          ))}
        </div>
        <span className="sq__tkt-count">{filtered.length} of {ticketsWithType.length} tickets</span>
        {hasActiveFilters && (
          <button className="sq__filter-clear" onClick={clearFilters}>✕ Clear column filters</button>
        )}
      </div>

      {/* ── type pills ── */}
      <div className="sq__tkt-type-pills">
        <span className="sq__tkt-type-label">Type:</span>
        <button
          className={`sq__tkt-typepill sq__tkt-typepill--all${typeFilter === 'all' ? ' active' : ''}`}
          onClick={() => setTypeFilter('all')}
        >
          All
        </button>
        {Object.entries(TICKET_TYPE_META).map(([k, v]) => (
          <button
            key={k}
            className={`sq__tkt-typepill sq__tkt-typepill--${k}${typeFilter === k ? ' active' : ''}`}
            onClick={() => setTypeFilter(k)}
          >
            {v.label}
            {typeCounts[k] ? <span className="sq__tkt-typepill-count">{typeCounts[k]}</span> : null}
          </button>
        ))}
      </div>

      <div className="sq__table-wrap" onContextMenu={openMenu}>
        <TableContextMenu {...menuState} onClose={closeMenu} onDownload={handleDownload} />
        <table className="sq__table sq__tkt-tbl">
          <thead>
            <tr>
              <th style={{ width: 28 }} />
              <SortTh col="time"          sortKey={sortKey} sortDir={sortDir} onSort={onSort}>Time (CST)</SortTh>
              <SortTh col="shortId"       sortKey={sortKey} sortDir={sortDir} onSort={onSort}>Ticket #</SortTh>
              <SortTh col="ticketName"    sortKey={sortKey} sortDir={sortDir} onSort={onSort}>Name</SortTh>
              <SortTh col="status"        sortKey={sortKey} sortDir={sortDir} onSort={onSort}>Status</SortTh>
              <SortTh col="ticketType"    sortKey={sortKey} sortDir={sortDir} onSort={onSort}>Type</SortTh>
              <SortTh col="itemQty"       sortKey={sortKey} sortDir={sortDir} onSort={onSort} className="sq__num">Items</SortTh>
              <SortTh col="revenueCents"  sortKey={sortKey} sortDir={sortDir} onSort={onSort} className="sq__num">Revenue</SortTh>
              <SortTh col="discountCents" sortKey={sortKey} sortDir={sortDir} onSort={onSort} className="sq__num">Discount</SortTh>
            </tr>
            <tr className="sq__filter-row">
              <td /><td />
              <FilterCell type="text"   fval={colFilters.shortId}       colKey="shortId"       setFilter={setColFilter} />
              <FilterCell type="namefilter" fval={colFilters.ticketName}    colKey="ticketName"    setFilter={setColFilter} />
              <td /><td />
              <FilterCell type="min"    fval={colFilters.itemQty}       colKey="itemQty"       setFilter={setColFilter} className="sq__num" />
              <FilterCell type="minmax" fval={colFilters.revenueCents}  colKey="revenueCents"  setFilter={setColFilter} className="sq__num" />
              <FilterCell type="min"    fval={colFilters.discountCents} colKey="discountCents" setFilter={setColFilter} className="sq__num" />
            </tr>
          </thead>
          <tbody>
            {filtered.map(tkt => (
              <React.Fragment key={tkt.id}>
                <tr
                  className={`sq__tkt-row${expanded === tkt.id ? ' open' : ''}${tkt.status === 'OPEN' ? ' sq__tkt-open' : ''}`}
                  onClick={() => setExpanded(expanded === tkt.id ? null : tkt.id)}
                >
                  <td className="sq__tkt-toggle">{expanded === tkt.id ? '▾' : '▸'}</td>
                  <td className="sq__tkt-time">{fmtTimeCst(tkt.time)}</td>
                  <td className="sq__tkt-id">{tkt.shortId}</td>
                  <td className="sq__tkt-name">{tkt.ticketName || <span className="sq__tkt-noname">—</span>}</td>
                  <td>
                    <span className={`sq__badge sq__badge--${tkt.status.toLowerCase()}`}>
                      {tkt.status === 'OPEN' ? '🟡 Open' : '🟢 Done'}
                    </span>
                  </td>
                  <td>
                    <span className={`sq__tkt-typebadge sq__tkt-typebadge--${tkt.ticketType}`}>
                      {TICKET_TYPE_META[tkt.ticketType].label}
                    </span>
                  </td>
                  <td className="sq__num">{fmtN(tkt.itemQty)}</td>
                  <td className="sq__num sq__revenue">{fmt$(tkt.revenue)}</td>
                  <td className="sq__num sq__discount">{tkt.discountCents > 0 ? fmt$(tkt.discount) : '—'}</td>
                </tr>
                {expanded === tkt.id && (
                  <tr className="sq__tkt-expand">
                    <td colSpan={9}>
                      <div className="sq__tkt-lines">
                        {tkt.lines.map((l, j) => {
                          const notes = Array.isArray(l.notes) ? l.notes.filter(Boolean) : [];
                          return (
                            <div key={j} className="sq__tkt-line-wrap">
                              <div className="sq__tkt-line">
                                <span className="sq__tkt-lname">{l.name}</span>
                                <span className="sq__tkt-lqty-badge">{l.qty}</span>
                                <span className="sq__tkt-lrev">{fmt$(l.revenue)}</span>
                              </div>
                              {notes.length > 0 && (
                                <div className="sq__tkt-lnotes">
                                  {notes.map((n, ni) => <span key={ni} className="sq__tkt-lnote">📝 {n}</span>)}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
          <tfoot>
            <tr className="sq__tfoot">
              <td colSpan={6}>Total ({filtered.length})</td>
              <td className="sq__num">{fmtN(tot.items)}</td>
              <td className="sq__num sq__revenue">{fmt$(tot.rev / 100)}</td>
              <td className="sq__num sq__discount">{tot.disc > 0 ? fmt$(tot.disc / 100) : '—'}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </>
  );
}
